import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    Pagination,
    IconButton,
    InputBase,
    Avatar,
    TableSortLabel,
} from '@mui/material';
import { CSVLink } from 'react-csv';
import FilterBar from './filterBar';
import { CustomButton, CustomModal, TableRowsSkeleton } from '../../shared';
import { ArrowLeftIcon, ArrowRightIcon, DownloadIcon } from '../../../assets';
import { partnersLogo } from '../../../assets/partners';
import SearchIcon from '@mui/icons-material/Search';
import {
    getEngagers,
    getAllPrescibers,
    getAllEngagers,
} from '../../../redux/thunks/engagementThunks';
import PrescribersProfile from '../dashboard/topPrescribers/prescriberProfile';
import { actions as prescriptionActions } from '../../../redux/reducers/prescriptionReducer';
import { actions as engagementActions } from '../../../redux/reducers/engagementReducer';
import {
    getSortedData,
    handleCustomisationForCompanyDashboard,
} from '../../../utility';
import {
    handlePrescribersHeading,
    handleTopEngagersApiCall,
} from '../../../utility/companySpecificFunctions';
import { exportProfileData } from '../../../redux/thunks/prescriptionThunks';
import {
    countEngagementsByChannel,
    countTotalTouchPoints,
    countTouchPointsByChannel,
    csvHeaders,
    getUniqueChannelsFromData,
    handleEngagementCount,
    rezlidhiaDefaultHeaders,
    TABLE_HEADERS,
} from './constants';
import { prodQaCompnies } from '../../../constants';

const TopPrecribers = (props) => {
    const {} = props;
    const [showModal, setShowModal] = useState(false);
    const [csvData, setCsvData] = useState({
        csvData: [],
        headers: [],
        target: '_blank',
        filename: '',
    });
    const dispatch = useDispatch();
    const {
        prescibersData,
        isLoading,
        currentPage,
        pageSize,
        totalCount,
        totalPages,
        searchText,
        npi_ids,
        totalEngagements,
    } = useSelector((state) => state.engagement);
    const { exportProfilesData, isExportDataLoading } = useSelector(
        (state) => state.prescription,
    );
    const { company } = useSelector((state) => state.company);
    const { startDateRange, endDateRange } = useSelector(
        (state) => state.filter,
    );
    const {
        userDetails: { is_admin },
    } = useSelector((state) => state.user);

    const { setSelectedNPI } = prescriptionActions;
    const { setCurrentPage, changeSearchText } = engagementActions;
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState(null);
    const fullExportCompanies = [];
    const showEngagements = [64, 66].includes(company.id);
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };
    const createSortHandler = (property) => () => {
        handleRequestSort(property);
    };

    const sortedData = React.useMemo(
        () => getSortedData(orderBy, order, prescibersData),
        [order, orderBy, prescibersData],
    );
    const partnersIconMapping = (name) => {
        const brand = name?.toLowerCase();
        for (const partnerName in partnersLogo) {
            if (brand.includes(partnerName)) {
                return partnersLogo[partnerName];
            }
        }
        return null;
    };

    const handlePageChange = (event, page) => {
        dispatch(setCurrentPage(page));
    };

    const handleNextClick = () => {
        if (currentPage < totalPages) {
            handlePageChange(_, currentPage + 1);
        }
    };

    const handlePreviousClick = () => {
        if (currentPage > 1) {
            handlePageChange(_, currentPage - 1);
        }
    };
    const handlePrescriberProfileModal = (npiId) => {
        if (company.id === 102) return;
        dispatch(setSelectedNPI(npiId));
        handleShowModal();
    };
    const handleShowModal = () => {
        setShowModal(!showModal);
    };

    const handleCSVData = () => {
        const csvName = handlePrescribersHeading(company.id)
            ? 'Top Engagers'
            : 'Top Prescribers';
        const csvData = [];
        const baseHeaders = prodQaCompnies.includes(company.id)
            ? rezlidhiaDefaultHeaders
            : csvHeaders;

        if (prodQaCompnies.includes(company.id) && exportProfilesData) {
            const headers = [
                ...baseHeaders,
                { label: 'Vendor-Channel', key: 'channel' },
                { label: 'Action', key: 'action' },
                { label: 'Date', key: 'date' },
            ];

            function formatDate(date) {
                if (!date) return '';
                const d = new Date(date);
                return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear()}`;
            }

            Object.values(exportProfilesData).forEach((providerData) => {
                const fieldEngagementData =
                    providerData?.extended_engagement_data[0]
                        .field_engagement_data;
                const baseRowData = {
                    npi_id: providerData.provider.npi_id,
                    name: `${providerData.provider.first_name} ${providerData.provider.last_name}`,
                    location: `${providerData.provider.address1} ${providerData.provider.city} ${providerData.provider.state} ${providerData.provider.zip} District:${providerData.provider.district}`,
                    district: providerData.provider.district,
                    specialty: providerData.provider.specialty,
                    segmentation: providerData.provider.segmentation,
                    triggered_date: providerData.triggered_date
                        .map(formatDate)
                        .join(', '),
                    triggered_type: providerData.triggered_type.join(', '),
                    total_trigger: providerData.total_trigger,
                    total_touch_points: countTotalTouchPoints(
                        providerData.total_points,
                        fieldEngagementData,
                    ),
                    total_engagements: handleEngagementCount(
                        providerData.engagement_data,
                        fieldEngagementData,
                    ),
                    first_referral: formatDate(providerData.first_rx_date),
                    last_rx_date: formatDate(providerData.last_rx_date),
                    total_rx: providerData.total_rx,
                    total_rx_value: providerData.total_rx_value,
                    partner_preference:
                        providerData.extended_engagement_data[0].partner_preference.join(
                            ', ',
                        ),
                    channel_preference:
                        providerData.extended_engagement_data[0].channel_preference.join(
                            ', ',
                        ),
                };

                // Add rows based on engagement data
                const engagementRows = providerData.engagement_data
                    .filter(
                        (engagement) =>
                            engagement.action && engagement.action_date,
                    ) // Filter out rows with blank data
                    .map((engagement) => ({
                        ...Object.fromEntries(
                            Object.keys(baseRowData).map((key) => [key, '']),
                        ),
                        channel:
                            engagement.engagement_vendor +
                            ' - ' +
                            engagement.engagement_channel,
                        action: engagement.action,
                        date: formatDate(engagement.action_date),
                    }))
                    .sort((a, b) => new Date(b.date) - new Date(a.date));

                if (Object.values(baseRowData).some((value) => value)) {
                    // Ensure the base row has data
                    csvData.push(baseRowData);
                }
                csvData.push(...engagementRows);
            });

            setCsvData({
                csvData: csvData,
                headers: headers,
                target: '_blank',
                filename: `${csvName}.csv`,
            });
        } else {
            let finalHeaders = showEngagements
                ? csvHeaders.filter(
                      (h) =>
                          h.key !== 'channel_preference' &&
                          h.key !== 'partner_preference',
                  )
                : csvHeaders;

            if (showEngagements) {
                const engagementsHeaders = [];
                for (let i = 1; i <= totalEngagements; i++) {
                    engagementsHeaders.push({
                        label: `Engagement ${i}`,
                        key: `Engagement ${i}`,
                    });
                }
                finalHeaders = [...finalHeaders, ...engagementsHeaders];
            }
            if (prescibersData && prescibersData.length > 0) {
                prescibersData.forEach((prescriber) => {
                    const {
                        npi_id,
                        first_name,
                        last_name,
                        address1,
                        city,
                        state,
                        zip,
                        district,
                        channel_preference,
                        partner_preference,
                        segmentation,
                        territory1,
                        specialty,
                        engagements = [],
                    } = prescriber;
                    const exportSegmentation = Array.isArray(segmentation)
                        ? segmentation.join(', ')
                        : segmentation;
                    const data = {
                        id: npi_id,
                        name: `${first_name} ${last_name}`,
                        location: `${address1}, ${city}, ${state}, ${zip}, District: ${district}`,
                        territory1,
                        specialty,
                        segmentation: exportSegmentation,
                    };
                    if (!showEngagements) {
                        data.channel_preference = Array.isArray(
                            channel_preference,
                        )
                            ? channel_preference.join(', ')
                            : channel_preference;
                        data.partner_preference = Array.isArray(
                            partner_preference,
                        )
                            ? partner_preference.join(', ')
                            : partner_preference;
                    }
                    if (showEngagements) {
                        for (let i = 0; i < totalEngagements; i++) {
                            const engagement = engagements[i];
                            if (engagement) {
                                data[`Engagement ${i + 1}`] =
                                    `${engagement.date} - ${engagement.channel_vendor.join(', ')}`;
                            } else {
                                data[`Engagement ${i + 1}`] = '';
                            }
                        }
                    }
                    csvData.push(data);
                });
            }

            setCsvData({
                csvData: csvData,
                headers: finalHeaders,
                target: '_blank',
                filename: `${csvName}.csv`,
            });
        }
    };

    const renderNoPrescriberData = () => (
        <TableRow>
            <TableCell colSpan={8}>
                <Box p={3} textAlign="center">
                    <Typography className="text-sm-regular gray-900">
                        No{' '}
                        {handlePrescribersHeading(company.id)
                            ? 'engager'
                            : 'prescriber'}{' '}
                        performance data to display
                    </Typography>
                </Box>
            </TableCell>
        </TableRow>
    );

    const renderPrescriberData = () =>
        totalCount > 0
            ? sortedData
                  .slice(
                      currentPage * pageSize - pageSize,
                      currentPage * pageSize,
                  )
                  ?.map((prescriber, index) => {
                      const fullName =
                          prescriber?.first_name + ' ' + prescriber?.last_name;
                      const location =
                          prescriber?.address1 +
                          ', ' +
                          prescriber?.city +
                          ', ' +
                          prescriber?.state +
                          ',' +
                          prescriber?.zip;

                      return (
                          <>
                              <TableRow
                                  className="cursor-pointer"
                                  key={prescriber.npi_id}
                                  onClick={() =>
                                      handlePrescriberProfileModal(
                                          prescriber.npi_id,
                                      )
                                  }
                              >
                                  <TableCell rowSpan={2}>
                                      <Typography className="text-xs-regular gray-500">
                                          {prescriber.npi_id}
                                      </Typography>
                                  </TableCell>
                                  <TableCell rowSpan={2}>
                                      <Typography className="text-xs-semi-bold gray-900">
                                          {fullName}
                                      </Typography>
                                  </TableCell>
                                  {/* Location and District */}
                                  <TableCell
                                      className="no-table-border-cell"
                                      rowSpan={1}
                                  >
                                      <Typography className="text-sm-regular gray-900">
                                          {location}
                                      </Typography>
                                  </TableCell>
                                  <TableCell
                                      className="table-row-data "
                                      rowSpan={2}
                                  >
                                      <Box className="flex-row-wrap gap-4">
                                          {prescriber?.channel_preference?.map(
                                              (channel) => (
                                                  <Box
                                                      className="br-16px bg-gray-100 padding-tb-4-rl-12"
                                                      key={channel}
                                                  >
                                                      <Typography className="text-xs-medium gray-700">
                                                          {channel}
                                                      </Typography>
                                                  </Box>
                                              ),
                                          )}
                                      </Box>
                                  </TableCell>
                                  <TableCell
                                      className="table-row-data "
                                      rowSpan={2}
                                  >
                                      <Box className="flex-row-wrap ">
                                          {prescriber?.partner_preference?.map(
                                              (partner) => {
                                                  if (!partner) {
                                                      return;
                                                  }
                                                  return (
                                                      <Avatar
                                                          alt={partner}
                                                          className="border-1-gray br-16px wh-24px"
                                                          key={partner}
                                                          src={partnersIconMapping(
                                                              partner,
                                                          )}
                                                      >
                                                          {partner?.charAt(0)}
                                                      </Avatar>
                                                  );
                                              },
                                          )}
                                      </Box>
                                  </TableCell>
                                  <TableCell rowSpan={2}>
                                      <Box className="flex-column-center br-16px padding-tb-4-rl-12 bg-primary-50">
                                          <Typography className="text-xs-medium primary-700">
                                              {prescriber?.segmentation}
                                          </Typography>
                                      </Box>
                                  </TableCell>
                              </TableRow>
                              <TableRow key={prescriber?.npi_id + '_district'}>
                                  <TableCell className="padding-0 " rowSpan={1}>
                                      <Typography className="text-sm-regular gray-500">
                                          <span className="text-sm-bold gray-500">
                                              District:{' '}
                                          </span>
                                          {prescriber?.district}
                                      </Typography>
                                  </TableCell>
                              </TableRow>
                          </>
                      );
                  })
            : renderNoPrescriberData();

    const handleSearchTextChange = (event) => {
        const text = event.target.value;
        dispatch(changeSearchText(text));
    };

    useEffect(() => {
        if (company.id) {
            if (handleTopEngagersApiCall(company.id)) {
                dispatch(
                    getAllEngagers({
                        companyId: company.id,
                        startRange: startDateRange,
                        endRange: endDateRange,
                    }),
                );
            } else {
                dispatch(
                    getAllPrescibers({ companyId: company.id, position: 0 }),
                );
            }
        }
    }, [company]);

    useEffect(() => {
        if (
            prodQaCompnies.includes(company.id) &&
            !isLoading &&
            npi_ids.length
        ) {
            dispatch(
                exportProfileData({
                    companyId: company.id,
                    data: { npi_ids: npi_ids },
                }),
            );
        }
    }, [company, npi_ids]);

    return (
        <>
            {is_admin && <FilterBar />}
            <Grid container className="flex-row" spacing={'24px'}>
                <Grid item xs={12}>
                    <Box className="flex-row-space-between-center pb-24px">
                        <Box>
                            <Typography className="display-xs-medium gray-900">
                                Top{' '}
                                {handlePrescribersHeading(company.id)
                                    ? 'Engagers'
                                    : 'Prescribers'}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: '12px' }}>
                            <Box>
                                <Paper
                                    className="flex-row-align-items-center border-1-gray-300  br-8px padding-tb-1-rl-2 no-box-shadow "
                                    component="form"
                                    sx={{
                                        width: {
                                            md: 200,
                                            lg: 250,
                                            xl: 320,
                                        },
                                    }}
                                >
                                    <IconButton
                                        aria-label="search"
                                        sx={{ p: '5px' }}
                                        type="button"
                                    >
                                        <SearchIcon />
                                    </IconButton>
                                    <InputBase
                                        inputProps={{
                                            'aria-label':
                                                'search top prescribers',
                                        }}
                                        placeholder="Search"
                                        sx={{ ml: 1, flex: 1 }}
                                        value={searchText}
                                        onChange={handleSearchTextChange}
                                    />
                                </Paper>
                            </Box>
                            <CSVLink
                                data={csvData.csvData}
                                filename={csvData.filename}
                                headers={csvData.headers}
                                target={csvData.target}
                            >
                                <CustomButton
                                    buttonColor="#AD509E"
                                    handleClick={() => handleCSVData()}
                                    icon={<DownloadIcon />}
                                    title="Export All Data"
                                    titleColor="#FFFFFF"
                                />
                            </CSVLink>
                        </Box>
                    </Box>
                    {/* Table Content */}
                    <Box className="box-sizing-border-box border-1-gray br-8px">
                        <Box>
                            <TableContainer
                                className="no-box-shadow "
                                component={Paper}
                            >
                                <Table className="table-border-collapse ">
                                    <TableHead className="bg-gray-50">
                                        <TableRow>
                                            {TABLE_HEADERS?.map((column) => (
                                                <TableCell
                                                    className={`text-xs-medium gray-500  ${
                                                        column.value ===
                                                        'Location'
                                                            ? 'padding-tb-12-rl-0'
                                                            : 'padding-tb-12-rl-24'
                                                    }`}
                                                    key={column.value}
                                                >
                                                    <TableSortLabel
                                                        active={
                                                            orderBy ===
                                                            column.key
                                                        }
                                                        direction={
                                                            orderBy ===
                                                            column.key
                                                                ? order
                                                                : 'asc'
                                                        }
                                                        onClick={createSortHandler(
                                                            column.key,
                                                        )}
                                                    >
                                                        {column.value}
                                                    </TableSortLabel>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {isLoading ? (
                                            <TableRowsSkeleton
                                                columns={6}
                                                rows={10}
                                            />
                                        ) : (
                                            renderPrescriberData()
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>

                        {/* Table Footer */}
                        {totalPages ? (
                            <Box className="padding-tb-16-rl-24 border-top-gray">
                                <Box className="flex-row-space-between-center">
                                    <Box>
                                        <CustomButton
                                            buttonColor="#FFFFFF"
                                            buttonDisabled={
                                                !(currentPage !== 1)
                                            }
                                            customStyle={{
                                                borderRadius: '8px',
                                            }}
                                            handleClick={handlePreviousClick}
                                            icon={<ArrowLeftIcon />}
                                            title="Previous"
                                            titleColor="#344054"
                                        />
                                    </Box>

                                    {/* Pagination */}
                                    <Box>
                                        <Pagination
                                            hideNextButton
                                            hidePrevButton
                                            count={totalPages}
                                            page={currentPage}
                                            shape="rounded"
                                            onChange={handlePageChange}
                                        />
                                    </Box>

                                    <Box>
                                        <CustomButton
                                            buttonColor="#FFFFFF"
                                            buttonDisabled={
                                                !(totalPages > currentPage)
                                            }
                                            customStyle={{
                                                borderRadius: '8px',
                                            }}
                                            handleClick={handleNextClick}
                                            tailIcon={<ArrowRightIcon />}
                                            title="Next"
                                            titleColor="#344054"
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        ) : null}
                    </Box>
                </Grid>
                {company.id !== 102 && (
                    <CustomModal
                        component={
                            <PrescribersProfile handleClose={handleShowModal} />
                        }
                        modalOpen={showModal}
                        onClose={handleShowModal}
                    />
                )}
            </Grid>
        </>
    );
};

export default TopPrecribers;
